import { MentionsInputProps } from "./MentionsInput.types";

export const extractMentionId = (mention: string): string => {
  // eslint-disable-next-line no-useless-escape
  const result = mention.replace(/\@\[[^\]]+\]\(([^)]+)\)/g, "$1");
  return result;
};

type MappingItem = {
  id: string;
  name: string;
};

export function convertFormula(
  formula: string,
  mappingArr: MappingItem[]
): string {
  // Tạo một map từ id sang name
  const idToName = mappingArr.reduce<Record<string, string>>((map, item) => {
    map[item.id] = item.name;
    return map;
  }, {});

  return formula.replace(/\b([A-Z0-9_]+)\b/g, (match) => {
    if (idToName[match]) {
      return `@[${idToName[match]}](${match})`;
    }
    return match;
  });
}

const MENTION_MARKUP_REGEX = /@\[([^\]]+)\]\(([^)]+)\)/g;

/**
 * Chuyển markup sang plain text (giống text hiển thị trong textarea),
 * kèm mask đánh dấu các ký tự thuộc mention.
 */
export const getPlainTextWithMentionMask = (
  markup: string
): { plainText: string; inMention: boolean[] } => {
  let plainText = "";
  const inMention: boolean[] = [];
  let lastIndex = 0;

  const appendText = (text: string, isMention: boolean) => {
    plainText += text;
    for (let i = 0; i < text.length; i += 1) inMention.push(isMention);
  };

  markup.replace(
    MENTION_MARKUP_REGEX,
    (match, display: string, _id: string, offset: number) => {
      appendText(markup.slice(lastIndex, offset), false);
      appendText(display, true);
      lastIndex = offset + match.length;
      return match;
    }
  );
  appendText(markup.slice(lastIndex), false);

  return { plainText, inMention };
};

/**
 * Tìm vị trí (trong plain text) của dấu ngoặc khớp với dấu ngoặc đang được bôi đen.
 * Bỏ qua các dấu ngoặc nằm trong tên mention. Trả về `null` nếu không khớp.
 */
export const findMatchingBracket = (
  markup: string,
  selectionStart: number,
  selectionEnd: number
): number | null => {
  if (selectionEnd - selectionStart !== 1) return null;

  const { plainText, inMention } = getPlainTextWithMentionMask(markup);
  const selectedChar = plainText[selectionStart];
  if (inMention[selectionStart]) return null;
  if (selectedChar !== "(" && selectedChar !== ")") return null;

  const step = selectedChar === "(" ? 1 : -1;
  let depth = 0;
  for (let i = selectionStart; i >= 0 && i < plainText.length; i += step) {
    if (!inMention[i]) {
      if (plainText[i] === selectedChar) depth += 1;
      else if (plainText[i] === "(" || plainText[i] === ")") depth -= 1;
      if (depth === 0) return i;
    }
  }
  return null;
};

/**
 * react-mentions chỉ lọc mảng `data` theo `display`.
 * Bọc lại để search theo cả `display` và `id` (code hiển thị trong dấu ngoặc).
 */
export const createMentionDataProvider = (
  data: MentionsInputProps["mentionData"]
): MentionsInputProps["mentionData"] => {
  if (!Array.isArray(data)) return data;
  return (query: string) => {
    const keyword = query.trim().toLowerCase();
    return data.filter(({ id, display }) =>
      [display, id].some((v) =>
        String(v ?? "")
          .toLowerCase()
          .includes(keyword)
      )
    );
  };
};
